import { createSelector } from "reselect";
import { cloneDeep, get } from "lodash";
import { getAtIdFromMatchProps } from "../selectorUtils.js";

import { ALL_IMAGE_TYPES } from "../../../constants/images.js";
import { ICON_MAP } from "../../../constants/iconMap.js";
import {
  RESOURCE_AT_TYPES,
  RESOURCE_URI_CHUNKS
} from "../../../constants/entity.js";

import operationSelector from "../operations/operationSelector.js";
import { getDisabledStateFromMarking } from "../../../utils/marking.js";
import resourceSelector from "../resources/resourceSelector.js";

// -- STATE QUERIES -- //

const entitiesDataMapQuery = state =>
  get(state, "ephemeralEntities.dataMap", null);

const regionsQuery = state => get(state, "regions.data", null);
const projectsQuery = state => get(state, "projects.data", null);
const providersQuery = state => get(state, "providers.data", null);
const timezonesDataMapQuery = state => get(state, "timezones.dataMap", null);

const ephemeralImagesQuery = state => {
  const dataMap = state.ephemeralEntities.dataMap;
  const dataIndex = state.ephemeralEntities.dataIndex;

  const imageIndex = dataIndex.filter(atId => {
    const image = dataMap[atId];
    return image ? isAnImageType(image) : false;
  });

  return imageIndex.map(atId => dataMap[atId]);
};

const entityFromMatchPropsQuery = (state, props, id) => {
  const entities = entitiesDataMapQuery(state, props);

  if (!entities) return null;

  const imageId = id ? id : getAtIdFromMatchProps(props);

  const entity = entities[imageId];

  if (!entity) return null;

  // manage resource dependencies
  const isResource = RESOURCE_AT_TYPES.find(type => type === entity["@type"]);
  if (isResource) {
    return resourceSelector.buildFullResource(entity, state);
  }

  return cloneDeep(entity);
};

const imageFromMatchPropsQuery = (state, props, id) => {
  const entities = entitiesDataMapQuery(state, props);
  if (!entities) return null;

  const imageId = id
    ? id
    : getAtIdFromMatchProps(props, RESOURCE_URI_CHUNKS.IMAGES);

  return entities[imageId];
};

const imageChargeFromPropsQuery = (state, props, id) => {
  const imageId = id
    ? id
    : getAtIdFromMatchProps(props, RESOURCE_URI_CHUNKS.IMAGES);

  // omission from the original entity is a permissions decision so don't alter that
  const result = get(state, `charge.dataMap.${imageId}`, null);

  return result;
};

const imageUsageFromPropsQuery = (state, props, id) => {
  const imageId = id
    ? id
    : getAtIdFromMatchProps(props, RESOURCE_URI_CHUNKS.IMAGES);

  // omission from the original entity is a permissions decision so don't alter that
  const result = get(state, `usage.dataMap.${imageId}`, null);

  return result;
};

const imageMeasurementsFromPropsQuery = (state, props, id) => {
  const imageId = id
    ? id
    : getAtIdFromMatchProps(props, RESOURCE_URI_CHUNKS.IMAGES);

  // omission from the original entity is a permissions decision so don't alter that
  return get(state, `measurements.dataMap.${imageId}`, null);
};

// -- PROPS QUERIES -- //

const entityFromPropsQuery = (state, props) => {
  return get(props, "data", null);
};

const regionsFromPropsQuery = (state, props) => {
  return get(props, "data.regions", []);
};

const serviceFromPropsQuery = (state, props) => {
  return get(props, "data.service", null);
};

// -- HELPERS -- //

function isAnImageType(image) {
  const isImage = ALL_IMAGE_TYPES.find(type => type === image["@type"]);
  return !!isImage;
}

function generateSimpleResourceMap(queryResult) {
  return queryResult.map(item => {
    return {
      key: item.id,
      value: item["@id"],
      service: item.services,
      text: item.name,
      icon: ICON_MAP[item.operatingSystem]
    };
  }, []);
}

// -- SELECTORS -- //

const selectImage = id => {
  return createSelector(
    [
      imageFromMatchPropsQuery,
      timezonesDataMapQuery,
      operationSelector.permissionsQuery,
      imageChargeFromPropsQuery,
      imageUsageFromPropsQuery,
      imageMeasurementsFromPropsQuery
    ],
    (baseImage, timezones, permissions, charge, usage, measurements) => {
      if (!baseImage) return null;

      const image = cloneDeep(baseImage);

      image.timezone = timezones[baseImage.timezone];

      if (charge) image.charge = charge;
      if (usage) image.usage = usage;
      if (measurements) image.measurements = measurements;

      return operationSelector.stampOperationPermissions(image, permissions);
    }
  );
};

const selectImageSource = id => {
  return createSelector(
    [imageFromMatchPropsQuery, entitiesDataMapQuery],
    (baseImage, entitiesMap) => {
      if (!baseImage || !baseImage.source || !entitiesMap) return null;

      return cloneDeep(entitiesMap[baseImage.source]);
    }
  );
};

const selectImageSourceByEntity = createSelector(
  [entityFromPropsQuery, entitiesDataMapQuery],
  (baseImage, entitiesMap) => {
    if (!baseImage || !baseImage.source || !entitiesMap) return null;
    return cloneDeep(entitiesMap[baseImage.source]);
  }
);

const selectImages = createSelector(
  [
    ephemeralImagesQuery,
    providersQuery,
    regionsQuery,
    timezonesDataMapQuery,
    operationSelector.permissionsQuery
  ],
  (ephemeralImages, providersData, regionsData, timezones, permissions) => {
    if (!ephemeralImages) return [];

    // expand images with required, sub entities
    const images = ephemeralImages.map(image => {
      const imageClone = cloneDeep(image);
      imageClone.provider = providersData.find(provider =>
        provider.services.includes(image.service)
      );
      imageClone.regions = regionsData.filter(region =>
        image.regions.includes(region["@id"])
      );
      imageClone.timezone = timezones[image.timezone];
      return imageClone;
    });

    return operationSelector.stampOperationPermissions(images, permissions);
  }
);

const selectEntityByMatchProps = createSelector(
  [entityFromMatchPropsQuery, operationSelector.permissionsQuery],
  (entity, permissions) => {
    if (!entity || !permissions) return null;

    return operationSelector.stampOperationPermissions(entity, permissions);
  }
);

const selectProviderByEntityService = createSelector(
  [serviceFromPropsQuery, providersQuery],
  (service, providers) => {
    if (!service || !providers) return null;

    const provider = providers.find(provider =>
      provider.services.includes(service)
    );

    return provider;
  }
);

const selectMarkingMessageByEntity = createSelector(
  [entityFromMatchPropsQuery],
  image => {
    if (!image || !image.marking) return null;
    return getDisabledStateFromMarking(image.marking, image["@type"]);
  }
);

const selectRegionsByEntity = createSelector(
  [regionsFromPropsQuery, regionsQuery],
  (regions, regionsData) => {
    if (!regions || !regionsData) return [];

    const filteredRegions = regionsData.filter(region =>
      regions.includes(region["@id"])
    );

    return filteredRegions;
  }
);

// -- DROPDOWN OPTIONS SELECTORS -- //

function filterInvalidRegions(regions, baseImage) {
  const serviceId = baseImage.service;
  const imageRegions = baseImage.regions;

  // remove readonly regions
  let filteredRegions = regions.filter(region => {
    return !region.readonly;
  });

  // check for invalid services
  if (serviceId) {
    filteredRegions = filteredRegions.filter(item =>
      item.services.some(element => element === serviceId)
    );
  }

  return filteredRegions;
}

const selectAvailableRegionsList = createSelector(
  [entityFromMatchPropsQuery, regionsQuery],
  (baseImage, regions) => {
    if (!baseImage) return [];

    const filteredRegions = filterInvalidRegions(regions, baseImage);

    return generateSimpleResourceMap(filteredRegions);
  }
);

const selectAvailableProjectsList = createSelector(
  [entityFromMatchPropsQuery, regionsQuery, projectsQuery],
  (baseImage, regions, projects) => {
    if (!baseImage) return [];

    const filteredRegions = filterInvalidRegions(regions, baseImage);

    const filteredProjects = projects.filter(project => {
      const projectRegion = get(project, "regions[0]", undefined);
      if (projectRegion) {
        return filteredRegions.some(region => region["@id"] === projectRegion);
      }
      // if no region is found show the project as we'll show a region selector in the UI
      return true;
    });

    return filteredProjects.map(item => {
      return {
        key: item.id,
        value: item.name,
        text: item.name,
        region: get(item, "regions[0]", undefined)
      };
    }, []);
  }
);

// using the following export pattern so we can mock more easily using jest

const imageSelector = {
  selectImage,
  selectImageSource,
  selectImageSourceByEntity,
  selectImages,
  selectEntityByMatchProps,
  selectProviderByEntityService,
  selectMarkingMessageByEntity,
  selectRegionsByEntity,
  selectAvailableRegionsList,
  selectAvailableProjectsList
};

export default imageSelector;
